import React, { useContext, useEffect, useRef } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import InfoMessage from "@components-shared/InfoMessageComponent";
import Currency from "@components-shared/Currency";
import { BetDetailsModalStoreContext } from "@state/context/my-bets/BetDetailsModalStoreContext";
import { CashoutStore } from "@state/stores/my-bets/CashoutStore";
import { BetDetailsModalStore } from "@state/stores/my-bets/BetDetailsModalStore";

export const AutoSellConfirmationBody = observer(
	function AutoSellConfirmationBody() {
		const thresholdInputRef = useRef<HTMLInputElement>(null);

		const { t } = useTranslation();

		let cashoutStore: CashoutStore;

		const store = useContext(BetDetailsModalStoreContext);

		if (store instanceof BetDetailsModalStore) {
			cashoutStore = store.myBetsStore.cashoutStore;
		} else {
			cashoutStore = store.cashoutStore;
		}

		useEffect(() => {
			const timer = setTimeout(() => {
				thresholdInputRef.current?.focus();
			}, 1);
			return () => clearTimeout(timer);
		}, []);

		if (!store) {
			console.error("Expected store, got null");
			return null;
		}

		if (cashoutStore.isAutosellModalCashedOut) {
			return (
				<InfoMessage
					type="success"
					message={t("mybets:SUCCESS_MESSAGE", {
						amount: cashoutStore.autosellModalCashoutGain,
						currency: App.state.rootStore.userAuthStore.currency,
					})}
				/>
			);
		} else if (cashoutStore.isAutocashoutUnavailable) {
			return (
				<>
					{cashoutStore.isConfigurationExists &&
						cashoutStore.secureWinAmount && (
							<div className="form__field u-mar--reset popup__footer__row">
								<label
									htmlFor="definedAutoCashoutAmount"
									className="label"
								>
									{t("mybets:CASHOUT.SECURE_WIN_DESCRIPTION")}
								</label>
								<input
									id="definedAutoCashoutAmount"
									className="input input--base"
									type="number"
									value={cashoutStore.secureWinAmount}
									disabled
								/>
							</div>
						)}
					<InfoMessage
						type="warning"
						message={t("mybets:CASHOUT.AUTO_CASHOUT_UNAVAILABLE")}
					/>
				</>
			);
		} else {
			return (
				<>
					<div className="form__field">
						<label htmlFor="setAutoCashoutAmount" className="label">
							{t("mybets:CASHOUT.SECURE_WIN_DESCRIPTION")}:
						</label>
						<input
							id="setAutoCashoutAmount"
							className={classNames("input input--base", {
								"input-validation-error":
									!cashoutStore.isAutosellFormValid,
							})}
							type="number"
							step=".01"
							min="0"
							value={cashoutStore.secureWinAmount || ""}
							onChange={(e) =>
								cashoutStore.handleChange(e.target.value)
							}
							ref={thresholdInputRef}
						/>
					</div>
					<div className="form__field u-mar--bottom--reset u-type--sml">
						<div
							className={classNames("row", {
								"u-color--text--primary--secondary":
									cashoutStore.isMinThresholdValid,
								"u-color--sent--danger":
									!cashoutStore.isMinThresholdValid,
							})}
						>
							<div className="col col-sml-6">
								{t("mybets:CASHOUT.MIN")}:
							</div>
							<div className="col col-sml-6 u-type--right">
								{cashoutStore.minThreshold}{" "}
								<Currency component="span" />
							</div>
						</div>
						<div
							className={classNames("row", {
								"u-color--text--primary--secondary":
									cashoutStore.isMaxThresholdValid,
								"u-color--sent--danger":
									!cashoutStore.isMaxThresholdValid,
							})}
						>
							<div className="col col-sml-6">
								{t("mybets:CASHOUT.MAX")}:
							</div>
							<div className="col col-sml-6 u-type--right">
								{cashoutStore.maxThreshold}{" "}
								<Currency component="span" />
							</div>
						</div>
					</div>
				</>
			);
		}
	}
);
